import React, { useState, useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import SimpleModal from "./SimpleModal";
import PropTypes from "prop-types";
import { useChartsStore } from "../../store/chartStore";

/**
 * Point & Figure Settings Modal - ChartIQ Compatible
 *
 * Simplified configuration:
 * - Checkbox: "Calcular automaticamente" (boxSize = null)
 * - InputNumber: "Tamanho do Box" (boxSize = number)
 * - InputNumber: "Reversao" (reversal = number, default 3)
 */
const PointFigureSettingsModal = ({ visible, onHide, chartId }) => {
  const chart = useChartsStore((state) =>
    state.charts.find((c) => c.id === chartId),
  );
  const updateChartConfig = useChartsStore((state) => state.updateChartConfig);

  // Get current config or use defaults
  const currentConfig = chart?.pointFigureConfig || {
    boxSize: null,
    reversal: 3,
  };

  // State
  const [isAuto, setIsAuto] = useState(currentConfig.boxSize === null);
  const [boxSize, setBoxSize] = useState(currentConfig.boxSize || 1);
  const [reversal, setReversal] = useState(currentConfig.reversal || 3);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      const config = chart?.pointFigureConfig || { boxSize: null, reversal: 3 };
      setIsAuto(config.boxSize === null);
      setBoxSize(config.boxSize || 1);
      setReversal(config.reversal || 3);
    }
  }, [visible, chart?.pointFigureConfig]);

  const handleSave = () => {
    const pointFigureConfig = {
      boxSize: isAuto ? null : boxSize,
      reversal: reversal,
    };

    updateChartConfig(chartId, { pointFigureConfig });
    onHide();
  };

  return (
    <SimpleModal
      visible={visible}
      onHide={onHide}
      onConfirm={handleSave}
      title="Configuracoes do Ponto & Figura"
      confirmLabel="Aplicar"
      cancelLabel="Cancelar"
      size="small"
      contentStyle={{ overflow: "visible", minHeight: "250px" }}
    >
      <div
        className="p-fluid"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: "1rem 0",
        }}
      >
        {/* Auto checkbox */}
        <div className="p-field" style={{ marginBottom: 0 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            <Checkbox
              inputId="isAuto"
              checked={isAuto}
              onChange={(e) => setIsAuto(e.checked)}
            />
            <label
              htmlFor="isAuto"
              style={{ fontWeight: 500, cursor: "pointer" }}
            >
              Calcular automaticamente
            </label>
          </div>
          <small
            className="p-text-secondary"
            style={{
              display: "block",
              marginTop: "0.5rem",
              marginLeft: "2rem",
            }}
          >
            Calcula o tamanho do box para mostrar ~30 colunas na tela
          </small>
        </div>

        {/* Box size input */}
        <div className="p-field" style={{ marginBottom: 0 }}>
          <label
            htmlFor="boxSize"
            style={{
              marginBottom: "0.5rem",
              display: "block",
              fontWeight: 500,
              opacity: isAuto ? 0.5 : 1,
            }}
          >
            Tamanho do Box
          </label>
          <InputNumber
            id="boxSize"
            value={boxSize}
            onValueChange={(e) => setBoxSize(e.value)}
            minFractionDigits={0}
            maxFractionDigits={2}
            min={0.01}
            step={0.1}
            style={{ width: "100%" }}
            disabled={isAuto}
            data-evo-id="pnfsettingsmodal-input-boxsize"
          />
          <small
            className="p-text-secondary"
            style={{
              display: "block",
              marginTop: "0.5rem",
              opacity: isAuto ? 0.5 : 1,
            }}
          >
            Tamanho fixo de cada box em pontos
          </small>
        </div>

        {/* Reversal input */}
        <div className="p-field" style={{ marginBottom: 0 }}>
          <label
            htmlFor="reversal"
            style={{
              marginBottom: "0.5rem",
              display: "block",
              fontWeight: 500,
            }}
          >
            Reversao
          </label>
          <InputNumber
            id="reversal"
            value={reversal}
            onValueChange={(e) => setReversal(e.value)}
            min={1}
            max={10}
            step={1}
            style={{ width: "100%" }}
            data-evo-id="pnfsettingsmodal-input-reversal"
          />
          <small
            className="p-text-secondary"
            style={{ display: "block", marginTop: "0.5rem" }}
          >
            Numero de boxes necessarios para reverter a direcao (padrao: 3)
          </small>
        </div>
      </div>
    </SimpleModal>
  );
};

PointFigureSettingsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  chartId: PropTypes.string,
};

export default PointFigureSettingsModal;
